import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import AdminDataTable from './components/AdminDataTable';
import MainButton from '../../../shared/layout/buttons/MainButton';
import { deactivateAdminPlanOffer, fetchAdminPlanOffers, saveAdminPlanOffer, updateAdminPublicPlan, updateAdminPlanVisibility, setAdminPartnerPlan } from '../../../services/Http/AdminApi';

const emptyOfferForm = { organization_id: '', plan_code: 'enterprise', amount: '', valid_until: '' };

const AdminPlanOffers = () => {
    const [data, setData] = useState({ organizations: [], plans: [], entitlements: [], offers: [] });
    const [search, setSearch] = useState('');
    const [form, setForm] = useState(emptyOfferForm);
    const [planEdits, setPlanEdits] = useState({});
    const [customEntitlements, setCustomEntitlements] = useState({});
    const [saving, setSaving] = useState(false);
    const [savingPlan, setSavingPlan] = useState('');
    const [loading, setLoading] = useState(true);
    const [partnerOrganization, setPartnerOrganization] = useState('');
    const [savingPartner, setSavingPartner] = useState(false);
    const [savingVisibility, setSavingVisibility] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchAdminPlanOffers(search ? { search } : {});
            setData(result);
            setPlanEdits(Object.fromEntries((result.plans || [])
                .filter((plan) => plan.is_public && plan.code !== 'free')
                .map((plan) => [plan.code, {
                    price: (plan.price_cents / 100).toFixed(2),
                    discount: plan.discount_price_cents == null ? '' : (plan.discount_price_cents / 100).toFixed(2),
                    starts: plan.discount_starts_at?.slice(0, 10) || '',
                    ends: plan.discount_ends_at?.slice(0, 10) || '',
                }])));
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Could not load retailer plans and offers.');
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => { load(); }, [load]);

    const enterprisePlan = useMemo(() => data.plans.find((plan) => plan.code === 'enterprise'), [data.plans]);
    const enterpriseEntitlements = useMemo(() => data.entitlements || [], [data.entitlements]);

    useEffect(() => {
        const existing = data.offers.find((offer) => offer.organization_id === form.organization_id && offer.plan_code === form.plan_code);
        setCustomEntitlements(existing?.custom_entitlements || {});
        if (existing) {
            setForm((current) => ({
                ...current,
                amount: (existing.price_cents / 100).toFixed(2),
                valid_until: existing.valid_until?.slice(0, 10) || '',
            }));
        } else if (form.organization_id) {
            setForm((current) => ({ ...current, amount: '', valid_until: '' }));
        }
    }, [data.offers, form.organization_id, form.plan_code]);

    const updatePlan = async (plan) => {
        const edit = planEdits[plan.code];
        if (!edit) return;
        setSavingPlan(plan.code);
        try {
            await updateAdminPublicPlan(plan.code, {
                price_cents: Math.round(Number(edit.price) * 100),
                discount_price_cents: edit.discount === '' ? null : Math.round(Number(edit.discount) * 100),
                discount_starts_at: edit.discount && edit.starts ? `${edit.starts}T00:00:00Z` : null,
                discount_ends_at: edit.discount && edit.ends ? `${edit.ends}T23:59:59Z` : null,
            });
            toast.success(`${plan.name} pricing saved.`);
            await load();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Could not save plan pricing.');
        } finally {
            setSavingPlan('');
        }
    };

    const save = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const overrides = Object.fromEntries(Object.entries(customEntitlements)
                .filter(([key, value]) => !key.endsWith('__unlimited') && value !== '' && value !== null && value !== undefined)
                .map(([key, value]) => {
                    const definition = enterpriseEntitlements.find((item) => item.key === key);
                    if (definition?.value_type === 'integer' && value !== '*') return [key, Number(value)];
                    if (definition?.value_type === 'boolean' && value !== '') return [key, value === true || value === 'true'];
                    return [key, value];
                }));
            await saveAdminPlanOffer({
                organization_id: form.organization_id,
                plan_code: form.plan_code,
                price_cents: Math.round(Number(form.amount) * 100),
                currency: 'EUR',
                valid_until: form.valid_until ? `${form.valid_until}T23:59:59Z` : null,
                custom_entitlements: form.plan_code === 'enterprise' ? overrides : undefined,
            });
            toast.success('Private plan offer saved.');
            setCustomEntitlements({});
            setForm(emptyOfferForm);
            await load();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Could not save the private offer.');
        } finally {
            setSaving(false);
        }
    };

    const deactivate = async (offer) => {
        try {
            await deactivateAdminPlanOffer(offer.id);
            toast.success('Offer deactivated.');
            await load();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Could not deactivate the offer.');
        }
    };

    const changeVisibility = async (plan) => {
        setSavingVisibility(plan.code);
        try {
            await updateAdminPlanVisibility(plan.code, !plan.is_listed);
            await load();
            toast.success('Plan visibility saved. Existing subscriptions are unchanged.');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Could not save plan visibility.');
        } finally {
            setSavingVisibility('');
        }
    };

    const changePartner = async (organizationId, enabled) => {
        if (!window.confirm(enabled
            ? 'Assign the free Partner plan with unlimited catalogue products? No Stripe subscription or payment will be created.'
            : 'Remove Partner access and return this retailer to Free with a 30-day limit grace period?')) return;
        setSavingPartner(true);
        try {
            await setAdminPartnerPlan(organizationId, enabled);
            toast.success(enabled ? 'Free Partner plan assigned.' : 'Retailer returned to Free.');
            setPartnerOrganization('');
            await load();
        } catch (error) {
            const messages = Object.values(error?.response?.data?.errors || {}).flat();
            toast.error(messages[0] || error?.response?.data?.message || 'Could not change the Partner plan.');
        } finally {
            setSavingPartner(false);
        }
    };

    const columns = [
        { key: 'organization', label: 'Retailer', render: (row) => row.organization?.name || row.organization_id },
        { key: 'plan', label: 'Plan', render: (row) => row.plan?.name || row.plan_code },
        { key: 'price_cents', label: 'Contracted monthly price', render: (row) => `${(row.price_cents / 100).toFixed(2)} ${row.currency}` },
        { key: 'custom_entitlements', label: 'Custom terms', render: (row) => Object.keys(row.custom_entitlements || {}).length ? `${Object.keys(row.custom_entitlements).length} overrides` : 'Plan defaults' },
        { key: 'valid_until', label: 'Offer expires', render: (row) => row.valid_until ? new Date(row.valid_until).toLocaleDateString() : 'No expiry' },
        { key: 'active', label: 'Status', render: (row) => row.active ? 'Active' : 'Inactive' },
        { key: 'actions', label: '', render: (row) => row.active ? <SmallButton type="button" onClick={() => deactivate(row)}>Deactivate</SmallButton> : '—' },
    ];

    return (
        <PageContainer>
            <PageHeader><PageTitle>Retailer plans and pricing</PageTitle></PageHeader>
            <PageContent>
                <Section>
                    <SectionTitle>Plan visibility</SectionTitle>
                    <Intro>Hidden plans are omitted from the public pricing API, landing page and public plan selection. Existing subscriptions and explicitly assigned private offers remain valid. Free is offered separately during signup. The landing page may retain its previous cached listing for up to one hour (longer if the API is unavailable).</Intro>
                    <PricingTable>
                        <thead><tr><th>Plan</th><th>Public listing</th></tr></thead>
                        <tbody>{data.plans.filter((plan) => plan.code !== 'free').map((plan) => (
                            <tr key={plan.code}><td>{plan.name}</td><td><label>
                                <input type="checkbox" aria-label={`${plan.name} visible publicly`} checked={plan.is_listed} disabled={!!savingVisibility || loading} onChange={() => changeVisibility(plan)} />
                                {plan.is_listed ? ' Visible' : ' Hidden — admin only'}
                            </label></td></tr>
                        ))}</tbody>
                    </PricingTable>
                </Section>

                <Section>
                    <SectionTitle>Free Partner plan</SectionTitle>
                    <Intro>Admin-assigned, 0 EUR, no payment method or Stripe subscription. Unlimited catalogue products, one sync per day, other features as on Free. Existing paid or externally managed subscriptions must be resolved first.</Intro>
                    <Form onSubmit={(event) => { event.preventDefault(); changePartner(partnerOrganization, true); }}>
                        <Field><Label htmlFor="partner-search">Find partner retailer</Label><Input id="partner-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search organization name" /></Field>
                        <Field><Label htmlFor="partner-retailer">Partner retailer</Label><Select id="partner-retailer" required value={partnerOrganization} onChange={(event) => setPartnerOrganization(event.target.value)}>
                            <option value="">Select retailer</option>
                            {data.organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
                        </Select></Field>
                        <MainButton type="submit" disabled={savingPartner || !partnerOrganization || loading}>Assign free Partner plan</MainButton>
                    </Form>
                    <AdminDataTable loading={loading} rows={data.partner_subscriptions || []} emptyText="No Partner plans assigned" columns={[
                        { key: 'organization', label: 'Retailer', render: (row) => row.organization?.name || row.organization_id },
                        { key: 'price_cents', label: 'Billing', render: () => 'Free · no Stripe' },
                        { key: 'actions', label: '', render: (row) => <SmallButton type="button" disabled={savingPartner} onClick={() => changePartner(row.organization_id, false)}>Return to Free</SmallButton> },
                    ]} />
                </Section>
                <Section>
                    <SectionTitle>Public plan prices and discounts</SectionTitle>
                    <Intro>Set the regular price and an optional discounted price window for new checkouts. Once a subscription starts, its contracted amount stays fixed until the subscription is changed or cancelled.</Intro>
                    <PricingTable>
                        <thead><tr><th>Plan</th><th>Regular monthly price (EUR)</th><th>Discount price (EUR)</th><th>Starts</th><th>Ends</th><th /></tr></thead>
                        <tbody>
                            {data.plans.filter((plan) => plan.is_public && plan.code !== 'free').map((plan) => {
                                const edit = planEdits[plan.code] || {};
                                return (
                                    <tr key={plan.code}>
                                        <td><strong>{plan.name}</strong><small>{plan.has_active_discount ? `Now ${(plan.effective_price_cents / 100).toFixed(2)} EUR` : 'Standard price'}</small></td>
                                        <td><PriceInput aria-label={`${plan.name} regular monthly price`} type="number" min="0.01" step="0.01" value={edit.price || ''} onChange={(event) => setPlanEdits((current) => ({ ...current, [plan.code]: { ...edit, price: event.target.value } }))} /></td>
                                        <td><PriceInput aria-label={`${plan.name} discount price`} type="number" min="0.01" step="0.01" value={edit.discount || ''} onChange={(event) => setPlanEdits((current) => ({ ...current, [plan.code]: { ...edit, discount: event.target.value } }))} placeholder="No discount" /></td>
                                        <td><DateInput aria-label={`${plan.name} discount start date`} type="date" disabled={!edit.discount} value={edit.starts || ''} onChange={(event) => setPlanEdits((current) => ({ ...current, [plan.code]: { ...edit, starts: event.target.value } }))} /></td>
                                        <td><DateInput aria-label={`${plan.name} discount end date`} type="date" disabled={!edit.discount} value={edit.ends || ''} onChange={(event) => setPlanEdits((current) => ({ ...current, [plan.code]: { ...edit, ends: event.target.value } }))} /></td>
                                        <td><SmallButton type="button" disabled={savingPlan === plan.code} onClick={() => updatePlan(plan)}>{savingPlan === plan.code ? 'Saving…' : 'Save'}</SmallButton></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </PricingTable>
                </Section>

                <Section>
                    <SectionTitle>Organization-specific offer</SectionTitle>
                    <Intro>Assign a private price to one retailer. Enterprise offers can also override individual limits and features without changing the shared Enterprise plan.</Intro>
                    <Form onSubmit={save}>
                        <Field>
                            <Label htmlFor="retailer-search">Find retailer</Label>
                            <Input id="retailer-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search organization name" />
                        </Field>
                        <Field>
                            <Label htmlFor="retailer">Retailer</Label>
                            <Select id="retailer" required value={form.organization_id} onChange={(event) => setForm({ ...form, organization_id: event.target.value })}>
                                <option value="">Select retailer</option>
                                {data.organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
                            </Select>
                        </Field>
                        <Field>
                            <Label htmlFor="plan">Plan</Label>
                            <Select id="plan" required value={form.plan_code} onChange={(event) => setForm({ ...form, plan_code: event.target.value })}>
                                <option value="">Select plan</option>
                                {data.plans.filter((plan) => plan.code !== 'partner' && plan.code !== 'free').map((plan) => <option key={plan.code} value={plan.code}>{plan.name} · {plan.is_public ? `${(plan.effective_price_cents / 100).toFixed(2)} ${plan.currency}` : 'custom pricing'}</option>)}
                            </Select>
                        </Field>
                        <Field>
                            <Label htmlFor="amount">Contracted monthly price (EUR)</Label>
                            <Input id="amount" type="number" inputMode="decimal" min="0.01" step="0.01" required value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
                        </Field>
                        <Field>
                            <Label htmlFor="expiry">Offer expiry (optional)</Label>
                            <Input id="expiry" type="date" value={form.valid_until} onChange={(event) => setForm({ ...form, valid_until: event.target.value })} />
                        </Field>
                        {form.plan_code === 'enterprise' && (
                            <EntitlementEditor>
                                <EditorTitle>Enterprise features and limits</EditorTitle>
                                <EditorHint>Leave a field unchanged to use the shared Enterprise default. Custom values apply only to this retailer.</EditorHint>
                                {enterpriseEntitlements.map((item) => (
                                    <EntitlementRow key={item.key}>
                                        <div><EntitlementName>{item.name}</EntitlementName><EntitlementMeta>{item.key}{item.unit ? ` · ${item.unit}` : ''}</EntitlementMeta></div>
                                        <EntitlementControl>
                                            {item.value_type === 'integer' && <>
                                                <Input type="number" min="0" step="1" aria-label={`${item.name} custom limit`} disabled={customEntitlements[`${item.key}__unlimited`] === true} value={customEntitlements[item.key] === '*' ? '' : customEntitlements[item.key] ?? ''} placeholder="Use plan default" onChange={(event) => setCustomEntitlements((current) => ({ ...current, [item.key]: event.target.value }))} />
                                                <label><input type="checkbox" checked={customEntitlements[`${item.key}__unlimited`] === true || customEntitlements[item.key] === '*'} onChange={(event) => setCustomEntitlements((current) => ({ ...current, [item.key]: event.target.checked ? '*' : '', [`${item.key}__unlimited`]: event.target.checked }))} /> Unlimited</label>
                                            </>}
                                            {item.value_type === 'boolean' && <Select aria-label={`${item.name} custom setting`} value={customEntitlements[item.key] === undefined ? '' : String(customEntitlements[item.key])} onChange={(event) => setCustomEntitlements((current) => ({ ...current, [item.key]: event.target.value === '' ? '' : event.target.value === 'true' }))}><option value="">Use plan default</option><option value="true">Enabled</option><option value="false">Disabled</option></Select>}
                                            {item.value_type === 'enum' && <Select aria-label={`${item.name} custom level`} value={customEntitlements[item.key] ?? ''} onChange={(event) => setCustomEntitlements((current) => ({ ...current, [item.key]: event.target.value }))}><option value="">Use plan default</option>{item.allowed_values?.map((value) => <option key={value.value} value={value.value}>{value.label || value.value}</option>)}</Select>}
                                        </EntitlementControl>
                                    </EntitlementRow>
                                ))}
                            </EntitlementEditor>
                        )}
                        <MainButton type="submit" disabled={saving || !enterprisePlan}>{saving ? 'Saving…' : 'Save organization offer'}</MainButton>
                    </Form>
                    <AdminDataTable columns={columns} rows={data.offers} loading={loading} emptyText="No organization-specific offers configured" />
                </Section>
            </PageContent>
        </PageContainer>
    );
};

const Section = styled.section`margin-bottom: 2rem;`;
const SectionTitle = styled.h2`margin: 0 0 0.45rem; font-size: 1.15rem; color: ${({ theme }) => theme.colors.textPrimary || '#344054'};`;
const Intro = styled.p`margin: 0 0 1rem; color: ${({ theme }) => theme.colors.textSecondary || '#667085'};`;
const PricingTable = styled.table`width: 100%; border-collapse: collapse; font-size: 0.9rem; th, td { padding: 0.7rem 0.5rem; border-bottom: 1px solid #eaecf0; text-align: left; vertical-align: middle; } th { color: #667085; font-size: 0.78rem; font-weight: 600; } td strong, td small { display: block; } td small { margin-top: 0.2rem; color: #667085; } @media (max-width: 760px) { display: block; overflow-x: auto; white-space: nowrap; }`;
const Form = styled.form`display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 0.8rem; align-items: end; margin-bottom: 1.5rem;`;
const Field = styled.div`display: flex; flex-direction: column; gap: 0.35rem; min-width: 0;`;
const Label = styled.label`font-size: 0.82rem; font-weight: 600; color: ${({ theme }) => theme.colors.textPrimary || '#344054'};`;
const Input = styled.input`width: 100%; min-width: 0; box-sizing: border-box; padding: 0.62rem 0.7rem; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit;`;
const PriceInput = styled(Input)`min-width: 125px;`;
const DateInput = styled(Input)`min-width: 145px;`;
const Select = styled.select`width: 100%; min-width: 0; padding: 0.62rem 0.7rem; border: 1px solid #d0d5dd; border-radius: 6px; background: #fff; font: inherit;`;
const SmallButton = styled.button`border: 0; background: transparent; color: #175cd3; cursor: pointer; font: inherit; font-weight: 600; &:disabled { opacity: 0.55; cursor: wait; }`;
const EntitlementEditor = styled.div`grid-column: 1 / -1; border-top: 1px solid #eaecf0; padding-top: 1rem;`;
const EditorTitle = styled.h3`margin: 0 0 0.3rem; font-size: 0.98rem;`;
const EditorHint = styled.p`margin: 0 0 0.8rem; color: #667085; font-size: 0.86rem;`;
const EntitlementRow = styled.div`display: grid; grid-template-columns: minmax(190px, 1fr) minmax(220px, 1fr); gap: 1rem; align-items: center; padding: 0.65rem 0; border-bottom: 1px solid #f2f4f7; @media (max-width: 560px) { grid-template-columns: 1fr; gap: 0.4rem; }`;
const EntitlementName = styled.strong`display: block; font-size: 0.88rem;`;
const EntitlementMeta = styled.small`display: block; margin-top: 0.15rem; color: #667085;`;
const EntitlementControl = styled.div`display: flex; align-items: center; gap: 0.75rem; label { display: inline-flex; align-items: center; gap: 0.35rem; white-space: nowrap; font-size: 0.83rem; }`;

export default AdminPlanOffers;
